import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import SkeletonLoad from 'src/pages/components/SkeletonLoad';
import {
  CompanySettings,
  CompanyUser,
  getCompanySettings,
  getCompanyUsers,
  regenerateCompanySlug,
  setCompanyEnabled,
  setCompanyNotificationEmails,
} from 'src/apis/eezyguard';
import LinkQrPanel from 'src/pages/components/eezyguard/LinkQrPanel';
import { useEezyguardAdmin } from 'src/pages/components/eezyguard/common';

type Props = { companyId: string };

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function dedupeEmails(list: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  list.forEach((raw) => {
    const email = raw.trim().toLowerCase();
    if (email && EMAIL_RE.test(email) && !seen.has(email)) {
      seen.add(email);
      out.push(email);
    }
  });
  return out;
}

export default function EezyGuardTab({ companyId }: Props): JSX.Element {
  const { isAdmin, loading: loadingCap } = useEezyguardAdmin();
  const [settings, setSettings] = useState<CompanySettings | null>(null);
  const [users, setUsers] = useState<CompanyUser[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingEmails, setSavingEmails] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!companyId || !isAdmin) {
      setLoading(false);
      return;
    }
    Promise.all([
      getCompanySettings(Number(companyId)),
      getCompanyUsers(Number(companyId)).catch(() => [] as CompanyUser[]),
    ])
      .then(([s, u]) => {
        setSettings(s);
        setUsers(u);
      })
      .catch(() => setError('Erro ao carregar a configuração do EezyLine.'))
      .finally(() => setLoading(false));
  }, [companyId, isAdmin]);

  const userEmailOptions = useMemo(
    () => Array.from(new Set(users.map((u) => u.email).filter(Boolean))),
    [users]
  );

  const toggle = async (enabled: boolean) => {
    setSaving(true);
    setError('');
    try {
      const updated = await setCompanyEnabled(Number(companyId), enabled);
      setSettings((prev) => ({ ...(prev as CompanySettings), ...updated }));
    } catch {
      setError('Não foi possível atualizar o EezyLine.');
    } finally {
      setSaving(false);
    }
  };

  const saveEmails = async (emails: string[]) => {
    const normalized = dedupeEmails(emails);
    setSavingEmails(true);
    setError('');
    try {
      const updated = await setCompanyNotificationEmails(
        Number(companyId),
        normalized
      );
      setSettings((prev) => ({ ...(prev as CompanySettings), ...updated }));
    } catch {
      setError('Não foi possível salvar a lista de e-mails.');
    } finally {
      setSavingEmails(false);
    }
  };

  const regenerate = async () => {
    if (!window.confirm('Regenerar o link invalida o link anterior. Continuar?')) {
      return;
    }
    setRegenerating(true);
    try {
      const updated = await regenerateCompanySlug(Number(companyId));
      setSettings((prev) => ({ ...(prev as CompanySettings), ...updated }));
    } catch {
      setError('Não foi possível regenerar o link.');
    } finally {
      setRegenerating(false);
    }
  };

  if (loadingCap || loading) return <SkeletonLoad variant="cards" rows={2} />;

  if (!isAdmin) {
    return (
      <Alert severity="warning">
        Apenas administradores EezyLine autorizados podem configurar este módulo.
      </Alert>
    );
  }

  const emails = settings?.notification_emails ?? [];

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {error && <Alert severity="error">{error}</Alert>}
      <Typography variant="body2" color="text.secondary">
        Ative o canal de denúncias EezyLine para esta empresa. O link público é gerado
        automaticamente na primeira ativação e preservado em reativações.
      </Typography>
      <FormControlLabel
        control={
          <Switch
            checked={Boolean(settings?.enabled)}
            disabled={saving}
            onChange={(e) => toggle(e.target.checked)}
          />
        }
        label={settings?.enabled ? 'EezyLine ativado' : 'EezyLine desativado'}
      />

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          E-mails de notificação e acesso
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={1}>
          Estes e-mails recebem as notificações de denúncias e têm acesso ao módulo
          desta empresa. Selecione da lista de usuários ou digite um e-mail.
        </Typography>
        <Autocomplete
          multiple
          freeSolo
          options={userEmailOptions}
          value={emails}
          inputValue={emailInput}
          disabled={savingEmails}
          onInputChange={(_, newInput) => {
            // Vírgula finaliza o e-mail atual e adiciona à lista.
            if (newInput.includes(',')) {
              const tokens = newInput.split(',');
              const toAdd = tokens.slice(0, -1);
              const remainder = tokens[tokens.length - 1];
              const merged = dedupeEmails([...emails, ...toAdd]);
              if (merged.length !== emails.length) {
                saveEmails(merged);
              }
              setEmailInput(remainder);
            } else {
              setEmailInput(newInput);
            }
          }}
          onChange={(_, newValue) => {
            saveEmails(newValue as string[]);
            setEmailInput('');
          }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                size="small"
                variant="outlined"
                label={option}
                {...getTagProps({ index })}
                key={option}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder="email@empresa.com"
              helperText="Para inserir outro e-mail, adicione uma vírgula (,) ou pressione Enter."
              inputProps={{
                ...params.inputProps,
                autoComplete: 'off',
              }}
            />
          )}
        />
      </Box>

      <LinkQrPanel
        slug={settings?.public_slug ?? null}
        enabled={Boolean(settings?.enabled)}
        enabledAt={settings?.enabled_at}
        canRegenerate={Boolean(settings?.public_slug)}
        onRegenerate={regenerate}
        regenerating={regenerating}
      />
    </Box>
  );
}
